import Drawer from '@mui/material/Drawer';
import { create, getOne, getList } from './../../../common/v4/BackendProvider';
import { EditPageSelect, EditPageButton, EditPageCheckbox, EditPageTextField } from './../../../common/EditPage';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DialerContainer from './DialerContainer';
import { usePermissions } from './../../../common/hooks/usePermissions';
import './drawerPane.css';


export default function DrawerPane(props) {
    const [client, setClient] = useState([]);
    const [contact, setContact] = useState([]);
    const [data, setData] = useState({});
    const [inputValues, setInputValues] = useState({});
    const [transcripts, setTranscripts] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [inCall, setInCall] = useState(false);
    const [overrideDestination, setOverrideDestination] = useState(false);
    const wsRef = useRef(null);
    const transcriptEndRef = useRef(null);
    const dialerContainerRef = useRef(null);
    const { task_id } = useParams();
    const { config } = usePermissions();
    

    
    // Check if WebRTC is connected for making test calls
    const isWebRTCConnected = false;
    
    // Function to sort transcripts by timestamp
    const sortTranscriptsByTimestamp = (transcripts) => {
        return transcripts.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateA - dateB;
        });
    };
    
    // Function to scroll to bottom of transcript
    const scrollToBottom = () => {
        transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Scroll to bottom when new transcripts arrive
    useEffect(() => {
        scrollToBottom();
    }, [transcripts]);

    useEffect(() => {
        getList('/admin/clients/listoptions').then(response => {
            setClient(response.data.data);
            setContact(0);
        });
    }, [props.id]);

    // Load destination number from parent component (AiTasksEdit) settings when drawer opens
    useEffect(() => {
        if (props.open && props.settings && props.settings.destination_number) {
            setData(prevData => ({
                ...prevData,
                destinationNumber: props.settings.destination_number
            }));
            setOverrideDestination(false);
        }
    }, [props.open, props.settings]);

    // Callback function to receive call state changes from DialerContainer
    const handleCallStateChange = (newInCallState) => {
        setInCall(newInCallState);
    };

    useEffect(() => {
        if (!data?.client) {
            setContact([]);
            return;
        }

        getOne('/admin/contacts/listcontactoptions/', data.client).then(response => {
            setContact(response.data.data);
        }).catch(() => {
            setContact([]);
        });
    }, [data?.client]);

    // WebSocket connection setup
    useEffect(() => {
        if (props.open && task_id) {
            const wsUrl = config.wsUrl;
            wsRef.current = new WebSocket(wsUrl);

            wsRef.current.onopen = () => {
                setIsConnected(true);
            };

            wsRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'transcript') {
                        setTranscripts(prev => {
                            const lastTranscript = prev[prev.length - 1];
                            
                            // If this is a continuation of the last transcript with the same transcript_id
                            if (lastTranscript && 
                                lastTranscript.transcript_id === data.transcript.transcript_id && 
                                !data.transcript.endOfSentence) {
                                // Append to the last transcript (keep it partial)
                                const updatedTranscripts = [
                                    ...prev.slice(0, -1),
                                    {
                                        ...lastTranscript,
                                        text: lastTranscript.text + ' ' + data.transcript.text,
                                        timestamp: lastTranscript.timestamp, // Preserve original timestamp
                                        isPartial: true
                                    }
                                ];
                                
                                // Sort by timestamp
                                return sortTranscriptsByTimestamp(updatedTranscripts);
                            } else if (lastTranscript && 
                                     lastTranscript.transcript_id === data.transcript.transcript_id && 
                                     data.transcript.endOfSentence) {
                                // Complete the last transcript (remove partial status)
                                const updatedTranscripts = [
                                    ...prev.slice(0, -1),
                                    {
                                        ...lastTranscript,
                                        text: lastTranscript.text + ' ' + data.transcript.text,
                                        timestamp: lastTranscript.timestamp, // Preserve original timestamp
                                        isPartial: false
                                    }
                                ];
                                
                                // Sort by timestamp
                                return sortTranscriptsByTimestamp(updatedTranscripts);
                            } else {
                                // New speaker or new transcript - complete any existing partial transcript first
                                let updatedPrev = prev;
                                if (lastTranscript && lastTranscript.isPartial) {
                                    // Complete the previous speaker's partial transcript
                                    updatedPrev = [
                                        ...prev.slice(0, -1),
                                        {
                                            ...lastTranscript,
                                            isPartial: false
                                        }
                                    ];
                                }
                                
                                // Add the new transcript
                                const updatedTranscripts = [...updatedPrev, {
                                    speaker: data.transcript.speaker,
                                    text: data.transcript.text,
                                    timestamp: data.transcript.timestamp,
                                    transcript_id: data.transcript.transcript_id,
                                    isPartial: !data.transcript.endOfSentence
                                }];
                                
                                // Check if this is a system message indicating call ended
                                if (data.transcript.speaker === 'system' && data.transcript.text === 'Call ended') {
                                    setInCall(false);
                                }
                                
                                // Sort by timestamp
                                return sortTranscriptsByTimestamp(updatedTranscripts);
                            }
                        });
                    } else if (data.type === 'call_start') {
                        setTranscripts(prev => {
                            const updatedTranscripts = [...prev, {
                                speaker: 'System',
                                text: 'Call started',
                                timestamp: data.timestamp,
                                isSystem: true
                            }];
                            return sortTranscriptsByTimestamp(updatedTranscripts);
                        });
                    } else if (data.type === 'call_end') {
                        setTranscripts(prev => {
                            const updatedTranscripts = [...prev, {
                                speaker: 'System',
                                text: `Call ended${data.reason ? `: ${data.reason}` : ''}`,
                                timestamp: data.timestamp,
                                isSystem: true
                            }];
                            return sortTranscriptsByTimestamp(updatedTranscripts);
                        });
                    } else if (data.type === 'function_call') {
                        const functionCall = data.function_call;
                        let argumentsText = '';
                        try {
                            const parsedArgs = JSON.parse(functionCall.arguments);
                            argumentsText = Object.entries(parsedArgs)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join(', ');
                        } catch (e) {
                            argumentsText = functionCall.arguments;
                        }
                        
                        setTranscripts(prev => {
                            const updatedTranscripts = [...prev, {
                                speaker: 'Function',
                                text: `${functionCall.name}(${argumentsText})`,
                                timestamp: data.timestamp,
                                isFunction: true
                            }];
                            return sortTranscriptsByTimestamp(updatedTranscripts);
                        });
                    } else if (data.type === 'error') {
                        // Handle error messages
                        const errorMessage = data.error?.message || 'Unknown error occurred';
                        const errorSpeaker = data.error?.speaker || 'System';
                        
                        setTranscripts(prev => {
                            const updatedTranscripts = [...prev, {
                                speaker: errorSpeaker.charAt(0).toUpperCase() + errorSpeaker.slice(1),
                                text: `Error: ${errorMessage}`,
                                timestamp: data.timestamp,
                                isError: true
                            }];
                            return sortTranscriptsByTimestamp(updatedTranscripts);
                        });
                        
                        // Also show a toast notification for errors
                        toast.error(`Call Error: ${errorMessage}`);
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            wsRef.current.onerror = (error) => {
                console.error('WebSocket error:', error);
                toast.error('Error connecting to transcript service');
            };

            wsRef.current.onclose = () => {
                setIsConnected(false);
            };

            return () => {
                if (wsRef.current) {
                    wsRef.current.close();
                }
            };
        }
    }, [props.open, task_id]);

    const onChange = (id, value) => {
        setData({ ...data, [id]: value });
    }

    const onInputValueChange = (fieldName, value) => {
        setInputValues({ ...inputValues, [fieldName]: value });
    }

    const makeTestCall = () => {
        // Validate destination number if override is enabled
        if (overrideDestination && (!data.destinationNumber || data.destinationNumber.trim() === '')) {
            toast.error('Destination number is required when override is enabled');
            return;
        }

        // Immediately set inCall to true to disable the button
        setInCall(true);
        // Immediately clear previous transcripts when starting a new call
        setTranscripts([]);
        
        const testdata = {
            contact_id: data.contactid,
            list_id: data.list,
            task_id: task_id,
            settings: props.settings,
            override_destination: overrideDestination,
            ...(overrideDestination && { destination_number: data.destinationNumber }),
            ...(props.settings?.task_type === 'text' && Object.keys(inputValues).length > 0 && { input_values: inputValues })
        }
        create('/admin/ai/tasks/testcall', {data: testdata}).then(response => {
            // Transcripts already cleared above
        }).catch(error => {
            toast.error('Error making test call: ' + error.response.data.message);
            // Re-enable the button if there's an error
            setInCall(false);
            if (dialerContainerRef.current && dialerContainerRef.current.doHangup) {
                dialerContainerRef.current.doHangup();
            }
        });
    }
    return (
        <Drawer anchor="right" open={props.open} onClose={props.onClose}>
            <div className="drawer-pane p-4" style={{width: '800px'}}>
                <div className="col-12">Select a client for testing</div>
                <div className="col-12">
                    <EditPageSelect label="Client" id="client" onChange={ onChange } value={ data.client } options={ client } />
                </div>
                <div className="col-12">
                    <EditPageSelect label="Contact" id="contactid" onChange={ onChange } value={ data.contactid } options={ contact } />
                </div>
                
                {/* Input Fields for Text Tasks */}
                {props.settings?.task_type === 'text' && props.settings?.input_fields && props.settings.input_fields.length > 0 && (
                    <div className="col-12">
                        <div className="form-group">
                            <label className="control-label"><strong>Task Input Fields</strong></label>
                            {props.settings.input_fields.map((fieldName, index) => {
                                // Get the label from contact_fields metadata
                                const fieldLabel = props.settings?.contact_fields?.[fieldName] || fieldName;
                                
                                return (
                                    <div key={index} className="mb-3">
                                        <label className="control-label">
                                            {fieldLabel}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder={`Enter ${fieldLabel.toLowerCase()}`}
                                            value={inputValues[fieldName] || ''}
                                            onChange={(e) => onInputValueChange(fieldName, e.target.value)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                
                <div className="col-12">
                    <EditPageCheckbox 
                        label="Override destination number" 
                        id="overrideDestination" 
                        onChange={(id, value) => setOverrideDestination(value)} 
                        value={overrideDestination} 
                    />
                    {overrideDestination && (
                        <EditPageTextField 
                            label="Destination Number" 
                            id="destinationNumber" 
                            onChange={onChange} 
                            value={data.destinationNumber || ''} 
                            placeholder="Enter destination number"
                            required
                        />
                    )}
                </div>
                <div className="col-12">
                    <div className="test-call-indicator-wrapper">
                        {/* WebRTC Connection Status */}
                        <small className={`badge test-call-indicator ${isWebRTCConnected ? 'bg-success' : 'bg-warning'}`}>
                            {isWebRTCConnected ? 'WebRTC Connected' : 'WebRTC Not Connected'}
                        </small>
                        {/* Call Status */}
                        <small className={`badge test-call-indicator ms-2 ${inCall ? 'bg-danger' : 'bg-secondary'}`}>
                            {inCall ? 'In Call' : 'Not In Call'}
                        </small>
                    </div>
                </div>
                <div className="col-12">   
                    {data.contactid > 0 && 
                        <EditPageButton 
                            handleClick={makeTestCall} 
                            value={
                                inCall ? "Call in Progress..." :
                                isWebRTCConnected ? "Save and Make Test Call" : 
                                "WebRTC Not Connected"
                            }
                            icon="phone"
                            bsStyle={
                                inCall ? "warning" :
                                isWebRTCConnected ? "primary" : 
                                "secondary"
                            }
                            className="btn-telephone"
                            disabled={!isWebRTCConnected || inCall || (overrideDestination && (!data.destinationNumber || data.destinationNumber.trim() === ''))}
                        />
                    }
                </div>
                <DialerContainer 
                    onCallStateChange={handleCallStateChange}
                    ref={dialerContainerRef}
                />
                {/* Transcript Display */}
                <div className="col-12 mt-4">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Call Transcript</h5>
                            {isConnected && <span className="badge bg-success" style={{ backgroundColor: '#198754', color: 'white' }}>Connected</span>}
                        </div>
                        <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {transcripts.length === 0 ? (
                                <p className="text-muted">No transcripts available yet...</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="transcript-table">
                                        <tbody>
                                            {transcripts
                                                .filter(transcript => transcript.text && transcript.text.trim() !== '')
                                                .map((transcript, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        {transcript.timestamp && (
                                                            <small>
                                                                {new Date(transcript.timestamp).toLocaleTimeString()}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`transcript-badge ${
                                                            transcript.isError ? 'error' :
                                                            transcript.isSystem ? 'system' : 
                                                            transcript.isFunction ? 'function' :
                                                            transcript.speaker.toLowerCase()
                                                        }`}>
                                                            {transcript.speaker.charAt(0).toUpperCase() + transcript.speaker.slice(1)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="transcript-text">
                                                            {transcript.text}
                                                            {transcript.isPartial && <span className="blinking-cursor">|</span>}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <div ref={transcriptEndRef} />
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
}